<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import type { TeacherProfile } from "@/stores/types";
import { apiUrl } from "@/config/api";

/**
 * UserLogin component for user authentication.
 * Emits events to communicate with the parent component (App.vue).
 */

interface LoginResponse {
  message: string;
  user: {
    id: number;
    email: string;
    name?: string;
  };
  profiles: TeacherProfile[];
}

interface LoginErrorResponse {
  error: string;
}

const props = defineProps<{
  signupSuccessMessage?: string;
}>();

const router = useRouter();
const authStore = useAuthStore();

const email = ref("");
const password = ref("");
const errorMessage = ref("");

const handleLogin = async () => {
  errorMessage.value = ""; // Reset error message
  try {
    const response = await fetch(apiUrl("/login"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include", // Send cookies with the request
      body: JSON.stringify({
        email: email.value,
        password: password.value,
      }),
    });

    if (!response.ok) {
      const errorData = (await response.json()) as LoginErrorResponse;
      throw new Error(errorData.error || "Kirjautuminen epäonnistui");
    }

    const data = (await response.json()) as LoginResponse;

    // Store user and profiles data in Pinia store
    authStore.login(data.user, data.profiles);

    // After login, always go to profile selection
    await router.push("/profile-selection");

    // // Check if there are profiles to select
    // if (data.profiles && data.profiles.length > 0) {
    //   await router.push("/profile-selection");
    // } else {
    //   // This case might need specific handling, e.g., if a user MUST have a profile.
    //   // For now, we can redirect to assessments, but it might trigger the guard again.
    //   // A better approach might be to guide the user to create a profile.
    //   await router.push("/assessments");
    // }
  } catch (error: unknown) {
    if (error instanceof Error) {
      errorMessage.value = error.message;
    } else {
      errorMessage.value = "Tapahtui odottamaton virhe.";
    }
  }
};
</script>

<template>
  <div class="auth-container">
    <div class="auth-form-card">
      <button class="close-button" @click="router.push('/')" aria-label="Sulje">
        ✕
      </button>

      <div class="auth-header">
        <h1 class="auth-title">Kirjaudu sisään tai Luo tili aloittaaksesi</h1>
      </div>

      <div v-if="props.signupSuccessMessage" class="success-message">
        {{ props.signupSuccessMessage }}
      </div>
      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>

      <form @submit.prevent="handleLogin" class="auth-form">
        <div class="form-group">
          <input
            id="login-email"
            v-model="email"
            type="email"
            placeholder="Mikä on sähköpostiosoitteesi?"
            required
            class="form-input"
          />
        </div>
        <div class="form-group">
          <input
            id="login-password"
            v-model="password"
            type="password"
            placeholder="Salasanasi?"
            required
            class="form-input"
          />
        </div>
        <button type="submit" class="submit-button">Kirjaudu sisään</button>
      </form>

      <div class="auth-footer">
        <!-- DISABLED: Signup button - accounts created through database seeding only -->
        <!-- <button @click="router.push('/signup')" class="footer-link">
          Luo Tili
        </button> -->
        <button class="footer-link">Unohtuiko salasana?</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.auth-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: var(--color-bg-secondary);
  padding: var(--space-md);
}

.auth-form-card {
  background: var(--color-bg-primary);
  padding: var(--space-2xl);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  width: 100%;
  max-width: 440px;
  position: relative;
}

.close-button {
  position: absolute;
  top: var(--space-md);
  right: var(--space-md);
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--color-text-light);
  cursor: pointer;
  padding: var(--space-xs);
  border-radius: var(--radius-sm);
  transition: all 0.2s ease-in-out;
}

.close-button:hover {
  color: var(--color-text-primary);
  background-color: var(--color-bg-tertiary);
}

.auth-header {
  text-align: center;
  margin-bottom: var(--space-xl);
}

.auth-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
  line-height: 1.3;
}

.error-message {
  background-color: #fef2f2;
  color: var(--color-error);
  border: 1px solid #fecaca;
  padding: var(--space-md);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-md);
  text-align: center;
  font-size: 0.875rem;
}

.success-message {
  background-color: #f0fdf4;
  color: var(--color-success);
  border: 1px solid #bbf7d0;
  padding: var(--space-md);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-md);
  text-align: center;
  font-size: 0.875rem;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  margin-bottom: var(--space-xl);
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-input {
  padding: var(--space-lg) var(--space-md);
  border: none;
  background-color: #e8e8e8;
  border-radius: 25px;
  font-size: 1rem;
  color: var(--color-text-primary);
  transition: all 0.2s ease-in-out;
  outline: none;
}

.form-input::placeholder {
  color: var(--color-text-light);
  font-size: 0.95rem;
}

.form-input:focus {
  background-color: #e0e0e0;
  box-shadow: 0 0 0 3px rgba(233, 30, 120, 0.1);
}

.submit-button {
  padding: var(--space-lg) var(--space-md);
  border: none;
  background-color: var(--color-primary);
  color: var(--color-text-white);
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: var(--space-md);
  transition: all 0.2s ease-in-out;
  box-shadow: var(--shadow-sm);
}

.submit-button:hover {
  background-color: var(--color-primary-dark);
  box-shadow: var(--shadow-md);
  transform: translateY(-1px);
}

.auth-footer {
  display: flex;
  justify-content: space-between;
  gap: var(--space-md);
  flex-wrap: wrap;
}

.footer-link {
  background: none;
  border: none;
  color: var(--color-primary);
  font-weight: 500;
  cursor: pointer;
  font-size: 0.875rem;
  padding: var(--space-xs);
  border-radius: var(--radius-sm);
  transition: all 0.2s ease-in-out;
}

.footer-link:hover {
  color: var(--color-primary-dark);
  background-color: var(--color-primary-lighter);
}

@media (max-width: 480px) {
  .auth-form-card {
    padding: var(--space-xl);
  }

  .auth-footer {
    flex-direction: column;
    align-items: center;
    gap: var(--space-sm);
  }
}
</style>
